import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import type { RegisterInput } from '../../contexts/AuthContext'
import './auth.css'

const POSITION_OPTIONS = [
  'Owner',
  'Manager',
  'Cashier',
  'Staff',
  'Other',
]

export default function RegisterPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [position, setPosition] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [placeName, setPlaceName] = useState('')
  const [address, setAddress] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  function validate(): string | null {
    if (!name.trim()) return 'Please enter your name.'
    if (!email.trim()) return 'Please enter your email.'
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) return 'Please enter a valid email address.'
    if (!position.trim()) return 'Please select or enter your position.'
    if (!companyName.trim()) return 'Please enter your company name.'
    if (!placeName.trim()) return 'Please enter the name of your place.'
    if (password.length < 6) return 'Password must be at least 6 characters.'
    if (password !== confirmPassword) return 'Passwords do not match.'
    return null
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    if (!auth) {
      setError('Registration is not configured. Add VITE_FIREBASE_* to your .env file.')
      return
    }
    if (auth.authError) {
      setError(auth.authError)
      return
    }
    setSubmitting(true)
    try {
      const input: RegisterInput = {
        name: name.trim(),
        email: email.trim(),
        position: position.trim(),
        companyName: companyName.trim(),
        placeName: placeName.trim(),
        address: address.trim(),
        password,
      }
      const placeId = await auth.register(input)
      navigate(`/app/${placeId}/orders`, { replace: true })
    } catch (err: unknown) {
      const code = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : ''
      const rawMessage = (err as { message?: string })?.message ?? ''
      const message =
        code === 'auth/configuration-not-found'
          ? 'Firebase Auth is not configured. Check your .env (VITE_FIREBASE_*) and enable Authentication in Firebase Console.'
          : code === 'auth/email-already-in-use'
            ? 'This email is already registered.'
            : code === 'auth/invalid-email'
              ? 'Invalid email address.'
              : code === 'permission-denied'
                ? 'Could not create your place or company. Enable Firestore in Firebase Console, deploy firestore.rules, and ensure you are allowed to create documents in companies and places.'
                : rawMessage || 'Registration failed.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--register">
        <h1 className="auth-title">Create an account</h1>
        <p className="auth-subtitle">Register your business and get started</p>
        <form onSubmit={handleSubmit} className="auth-form">
          {(error || auth?.authError) && (
            <div className="auth-error" role="alert">
              {error || auth?.authError}
            </div>
          )}

          <fieldset className="auth-fieldset">
            <legend className="auth-legend">Your details</legend>
            <label className="auth-label">
              Name
              <input
                type="text"
                className="auth-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                autoFocus
              />
            </label>
            <label className="auth-label">
              Email
              <input
                type="email"
                className="auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            <label className="auth-label">
              Position
              <select
                className="auth-input"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
              >
                <option value="">Select position</option>
                {POSITION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>

          <fieldset className="auth-fieldset">
            <legend className="auth-legend">Your business</legend>
            <label className="auth-label">
              Company name
              <input
                type="text"
                className="auth-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                autoComplete="organization"
              />
            </label>
            <label className="auth-label">
              Place name
              <input
                type="text"
                className="auth-input"
                value={placeName}
                onChange={(e) => setPlaceName(e.target.value)}
                placeholder="Venue or outlet name"
              />
            </label>
            <label className="auth-label">
              Address
              <input
                type="text"
                className="auth-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                autoComplete="street-address"
                placeholder="Optional"
              />
            </label>
          </fieldset>

          <fieldset className="auth-fieldset">
            <legend className="auth-legend">Account security</legend>
            <label className="auth-label">
              Password
              <input
                type="password"
                className="auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={6}
              />
            </label>
            <label className="auth-label">
              Confirm password
              <input
                type="password"
                className="auth-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                minLength={6}
              />
            </label>
          </fieldset>

          <div className="auth-actions">
            <button
              type="submit"
              className="auth-btn auth-btn--primary"
              disabled={submitting}
            >
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
          </div>
        </form>
        <p className="auth-footer">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
